import React, { useEffect, useMemo, useState } from 'react';
import {
  Card,
  Form,
  Input,
  Button,
  Space,
  Typography,
  DatePicker,
  Select,
  InputNumber,
  Table,
  Tag,
  message,
  Spin,
  Switch,
  Checkbox,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import {
  SaveOutlined,
  SendOutlined,
  CheckOutlined,
  CloudUploadOutlined,
  ReloadOutlined,
} from '@ant-design/icons';
import stockCountService, { type StockCountLine } from '@/services/stockCount.service';
import { useWarehouses } from '@/hooks/useInventory';
import { DepartmentSelect } from '@/components/finance/DepartmentSelect';

const { Title, Text } = Typography;
const { TextArea } = Input;

const EDITABLE = new Set(['Draft', 'InProgress']);

const StockCountDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new';
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const queryClient = useQueryClient();
  const { data: warehouses = [] } = useWarehouses();
  const [lineEdits, setLineEdits] = useState<Record<string, number | null>>({});

  const { data: session, isLoading } = useQuery({
    queryKey: ['stock-count', id],
    queryFn: () => stockCountService.getById(id!),
    enabled: !isNew && !!id,
  });

  useEffect(() => {
    if (!session) return;
    form.setFieldsValue({
      warehouse_id: session.warehouse_id,
      count_date: session.count_date ? dayjs(session.count_date) : dayjs(),
      blind_count: session.blind_count,
      department_id: session.department_id,
      notes: session.notes,
    });
    const map: Record<string, number | null> = {};
    for (const l of session.lines || []) {
      map[l.id] = l.counted_quantity == null ? null : Number(l.counted_quantity);
    }
    setLineEdits(map);
  }, [session, form]);

  const editable = isNew || (session && EDITABLE.has(session.status));
  const showSystem = !session?.blind_count || !EDITABLE.has(session?.status || '');

  const createMut = useMutation({
    mutationFn: (body: Record<string, unknown>) => stockCountService.create(body),
    onSuccess: (row) => {
      message.success('Count session created');
      queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
      navigate(`/scm/inventory/stock-count/${row.id}`, { replace: true });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Create failed'),
  });

  const saveLinesMut = useMutation({
    mutationFn: () => {
      const lines = (session?.lines || []).map((l) => ({
        id: l.id,
        counted_quantity: lineEdits[l.id] ?? l.counted_quantity,
        notes: l.notes,
      }));
      return stockCountService.updateLines(id!, lines);
    },
    onSuccess: () => {
      message.success('Counts saved');
      queryClient.invalidateQueries({ queryKey: ['stock-count', id] });
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Save failed'),
  });

  const actionMut = useMutation({
    mutationFn: async (action: 'snapshot' | 'submit' | 'approve' | 'post' | 'cancel') => {
      if (action === 'snapshot') return stockCountService.snapshot(id!);
      if (action === 'submit') {
        if (editable) await saveLinesMut.mutateAsync();
        return stockCountService.submit(id!);
      }
      if (action === 'approve') return stockCountService.approve(id!);
      if (action === 'post') return stockCountService.post(id!);
      return stockCountService.cancel(id!);
    },
    onSuccess: (row, action) => {
      message.success(`${action} ok`);
      queryClient.invalidateQueries({ queryKey: ['stock-count', id] });
      queryClient.invalidateQueries({ queryKey: ['stock-counts'] });
      if (row?.id) queryClient.setQueryData(['stock-count', id], row);
    },
    onError: (e: any) => message.error(e?.response?.data?.message || e?.message || 'Action failed'),
  });

  const columns = useMemo(() => {
    const cols: any[] = [
      { title: '#', dataIndex: 'line_number', width: 56 },
      { title: 'Item', dataIndex: 'item_code', width: 120 },
      { title: 'Description', dataIndex: 'item_description', ellipsis: true },
      {
        title: 'Heat',
        dataIndex: 'heat_number',
        width: 100,
        render: (v: string) => v || '—',
      },
      {
        title: 'Dim',
        dataIndex: 'dimension',
        width: 90,
        render: (v: string) => v || '—',
      },
    ];
    if (showSystem) {
      cols.push({
        title: 'System',
        dataIndex: 'system_quantity',
        width: 100,
        render: (v: number) => Number(v).toFixed(3),
      });
    }
    cols.push({
      title: 'Counted',
      key: 'counted',
      width: 120,
      render: (_: unknown, row: StockCountLine) =>
        editable ? (
          <InputNumber
            style={{ width: '100%' }}
            value={lineEdits[row.id] ?? undefined}
            onChange={(v) =>
              setLineEdits((prev) => ({ ...prev, [row.id]: v == null ? null : Number(v) }))
            }
          />
        ) : (
          row.counted_quantity == null ? '—' : Number(row.counted_quantity).toFixed(3)
        ),
    });
    if (showSystem) {
      cols.push({
        title: 'Variance',
        key: 'var',
        width: 100,
        render: (_: unknown, row: StockCountLine) => {
          const counted = lineEdits[row.id] ?? row.counted_quantity;
          if (counted == null) return '—';
          const v = Number(counted) - Number(row.system_quantity);
          const color = v === 0 ? undefined : v > 0 ? 'green' : 'red';
          return <Text type={color as any}>{v.toFixed(3)}</Text>;
        },
      });
    }
    return cols;
  }, [editable, showSystem, lineEdits]);

  if (!isNew && isLoading) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <Spin />
      </div>
    );
  }

  return (
    <div style={{ padding: 24 }}>
      <Card>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Space wrap style={{ justifyContent: 'space-between', width: '100%' }}>
            <div>
              <Title level={4} style={{ margin: 0 }}>
                {isNew ? 'New stock count' : session?.count_number}
              </Title>
              {session && (
                <Space>
                  <Tag>{session.status}</Tag>
                  {session.stock_adjustment_id && (
                    <Button
                      type="link"
                      size="small"
                      onClick={() =>
                        navigate(`/scm/warehouse/stock-adjustment/${session.stock_adjustment_id}`)
                      }
                    >
                      Open adjustment
                    </Button>
                  )}
                </Space>
              )}
            </div>
            <Space wrap>
              {!isNew && EDITABLE.has(session?.status || '') && (
                <>
                  <Button icon={<SaveOutlined />} loading={saveLinesMut.isPending} onClick={() => saveLinesMut.mutate()}>
                    Save counts
                  </Button>
                  <Button
                    icon={<ReloadOutlined />}
                    loading={actionMut.isPending}
                    onClick={() => actionMut.mutate('snapshot')}
                  >
                    Re-snapshot
                  </Button>
                  <Button
                    icon={<SendOutlined />}
                    loading={actionMut.isPending}
                    onClick={() => actionMut.mutate('submit')}
                  >
                    Submit
                  </Button>
                </>
              )}
              {!isNew && ['Submitted', 'Draft', 'InProgress'].includes(session?.status || '') && (
                <Button
                  type="primary"
                  icon={<CheckOutlined />}
                  loading={actionMut.isPending}
                  onClick={() => actionMut.mutate('approve')}
                >
                  Approve
                </Button>
              )}
              {!isNew && session?.status === 'Approved' && (
                <Button
                  type="primary"
                  icon={<CloudUploadOutlined />}
                  loading={actionMut.isPending}
                  onClick={() => actionMut.mutate('post')}
                >
                  Post to stock
                </Button>
              )}
              {!isNew && session && session.status !== 'Posted' && session.status !== 'Cancelled' && (
                <Button danger loading={actionMut.isPending} onClick={() => actionMut.mutate('cancel')}>
                  Cancel
                </Button>
              )}
              <Button onClick={() => navigate('/scm/inventory/stock-count')}>Back</Button>
            </Space>
          </Space>

          <Form
            form={form}
            layout="vertical"
            disabled={!isNew && !editable}
            onFinish={(values) => {
              createMut.mutate({
                warehouse_id: values.warehouse_id,
                count_date: values.count_date?.format?.('YYYY-MM-DD') || values.count_date,
                blind_count: Boolean(values.blind_count),
                department_id: values.department_id || null,
                notes: values.notes || null,
                snapshot_on_create: values.snapshot_on_create !== false,
                include_zero: Boolean(values.include_zero),
              });
            }}
          >
            <Space wrap size="large" style={{ width: '100%' }}>
              <Form.Item name="warehouse_id" label="Warehouse" rules={[{ required: true }]} style={{ minWidth: 240 }}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={warehouses.map((w: any) => ({
                    value: w.id,
                    label: `${w.warehouse_code} — ${w.warehouse_name}`,
                  }))}
                />
              </Form.Item>
              <Form.Item name="count_date" label="Count date" rules={[{ required: true }]} initialValue={dayjs()}>
                <DatePicker />
              </Form.Item>
              <Form.Item name="department_id" label="Department" style={{ minWidth: 220 }}>
                <DepartmentSelect />
              </Form.Item>
              <Form.Item name="blind_count" label="Blind count" valuePropName="checked">
                <Switch />
              </Form.Item>
              {isNew && (
                <>
                  <Form.Item name="snapshot_on_create" label="Snapshot stock now" valuePropName="checked" initialValue={true}>
                    <Checkbox>Load SOH lines</Checkbox>
                  </Form.Item>
                  <Form.Item name="include_zero" label="Include zero qty" valuePropName="checked">
                    <Checkbox>Include zeros</Checkbox>
                  </Form.Item>
                </>
              )}
            </Space>
            <Form.Item name="notes" label="Notes">
              <TextArea rows={2} />
            </Form.Item>
            {isNew && (
              <Button type="primary" htmlType="submit" loading={createMut.isPending}>
                Create &amp; snapshot
              </Button>
            )}
          </Form>

          {!isNew && (
            <Table<StockCountLine>
              rowKey="id"
              size="small"
              pagination={{ pageSize: 50 }}
              columns={columns}
              dataSource={session?.lines || []}
              scroll={{ x: 900 }}
            />
          )}
        </Space>
      </Card>
    </div>
  );
};

export default StockCountDetailPage;
